import { z } from 'zod';
import { AREA_CODE_PATTERN, ASSET_STATUSES, ASSET_TYPE_CODES } from './constants';

const areaCode = z.string().regex(AREA_CODE_PATTERN, 'Kode wilayah tidak valid');
const assetTypeCode = z.enum(ASSET_TYPE_CODES as [string, ...string[]]);

/** `minLng,minLat,maxLng,maxLat` in WGS84. */
export const bboxSchema = z
  .string()
  .transform((s) => s.split(',').map(Number))
  .refine(
    (n) => n.length === 4 && n.every(Number.isFinite),
    'bbox harus berisi 4 angka: minLng,minLat,maxLng,maxLat',
  )
  .refine(
    ([minLng, minLat, maxLng, maxLat]) =>
      minLng! >= -180 &&
      maxLng! <= 180 &&
      minLat! >= -90 &&
      maxLat! <= 90 &&
      minLng! < maxLng! &&
      minLat! < maxLat!,
    'bbox di luar rentang koordinat',
  )
  .transform(([minLng, minLat, maxLng, maxLat]) => ({
    minLng: minLng!,
    minLat: minLat!,
    maxLng: maxLng!,
    maxLat: maxLat!,
  }));
export type Bbox = z.infer<typeof bboxSchema>;

export const assetListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(100).optional(),
  type: assetTypeCode.optional(),
  status: z.enum(ASSET_STATUSES).optional(),
  area: areaCode.optional(),
  bbox: bboxSchema.optional(),
  /** `1` lists the trash (soft-deleted in the last 30 days) instead. */
  trash: z
    .enum(['0', '1', 'true', 'false'])
    .optional()
    .transform((v) => v === '1' || v === 'true'),
});
export type AssetListQuery = z.infer<typeof assetListQuerySchema>;

export const mapFeaturesQuerySchema = z.object({
  area: areaCode.optional(),
  bbox: bboxSchema.optional(),
});
export type MapFeaturesQuery = z.infer<typeof mapFeaturesQuerySchema>;

const position = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
const ring = z.array(position).min(4, 'Poligon butuh minimal 3 titik');
const polygon = z.array(ring).min(1);

/** GeoJSON Polygon or MultiPolygon geometry for an area boundary. `null` clears it. */
export const areaBoundarySchema = z.object({
  boundary: z
    .discriminatedUnion('type', [
      z.object({ type: z.literal('Polygon'), coordinates: polygon }),
      z.object({ type: z.literal('MultiPolygon'), coordinates: z.array(polygon).min(1) }),
    ])
    .nullable(),
});
export type AreaBoundaryInput = z.infer<typeof areaBoundarySchema>;

/** API list response envelope. */
export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface AssetListItem {
  id: string;
  code: string;
  name: string;
  typeCode: string;
  status: string;
  areaCode: string | null;
  address: string | null;
  lng: number;
  lat: number;
  isSample: boolean;
  /** CPE only. */
  customerName: string | null;
  deletedAt: string | null;
  /** Monitoring status (online/warning/…) when the asset is monitored, else null. */
  monitor: string | null;
}

const trimBlank = (v: unknown) => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim()) : v);
const optText = (max: number, label: string) =>
  z.preprocess(trimBlank, z.string().max(max, `${label} maksimal ${max} karakter`).nullable().default(null));

/**
 * Create/update payload (full form). `details` is validated separately with
 * `assetDetailsSchema(type)`. `area: null` = take it from the area boundaries.
 */
export const assetInputSchema = z.object({
  type: assetTypeCode,
  code: z.preprocess(
    trimBlank,
    z
      .string()
      .max(40, 'Kode maksimal 40 karakter')
      .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, 'Kode hanya boleh huruf, angka, titik, garis bawah dan strip')
      .transform((s) => s.toUpperCase())
      .nullable()
      .default(null),
  ),
  name: z
    .string({ required_error: 'Nama wajib diisi' })
    .trim()
    .min(1, 'Nama wajib diisi')
    .max(120, 'Nama maksimal 120 karakter'),
  status: z.enum(ASSET_STATUSES).default('planned'),
  model: optText(120, 'Model'),
  description: optText(2000, 'Deskripsi'),
  address: optText(300, 'Alamat'),
  price: z.preprocess(
    (v) =>
      trimBlank(v) === null || v === undefined
        ? null
        : typeof v === 'string'
          ? Number(v.replace(/\./g, '').replace(',', '.'))
          : v,
    z
      .number({ invalid_type_error: 'Harga harus berupa angka' })
      .min(0, 'Harga tidak boleh negatif')
      .max(1e12)
      .nullable(),
  ),
  area: z.preprocess(trimBlank, areaCode.nullable().default(null)),
  lat: z
    .number({ required_error: 'Koordinat wajib diisi', invalid_type_error: 'Koordinat tidak valid' })
    .min(-90)
    .max(90),
  lng: z
    .number({ required_error: 'Koordinat wajib diisi', invalid_type_error: 'Koordinat tidak valid' })
    .min(-180)
    .max(180),
  details: z.record(z.unknown()).default({}),
});
export type AssetInput = z.infer<typeof assetInputSchema>;

export const assetMoveSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

export const bulkDeleteSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
  /** Also remove port links. Without it, assets that are still linked are skipped. */
  force: z.boolean().default(false),
});

/** Parses "lat, lng" as pasted from Google Maps (also accepts a space or semicolon). */
export function parseLatLng(text: string): { lat: number; lng: number } | null {
  const m = text.trim().match(/^(-?\d+(?:\.\d+)?)\s*[,; ]\s*(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

export interface AssetDetail {
  id: string;
  code: string;
  name: string;
  typeCode: string;
  status: string;
  model: string | null;
  description: string | null;
  address: string | null;
  price: number | null;
  areaCode: string | null;
  lng: number;
  lat: number;
  isSample: boolean;
  isAutoGenerated: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  details: Record<string, string | number | null>;
  ports: Array<{ type: string; total: number; used: number; reserved: number; maintenance: number }>;
}

export interface AssetHistoryEntry {
  id: number;
  action: string;
  at: string;
  user: string | null;
  diff: unknown;
}

export interface AreaSummary {
  code: string;
  name: string;
  prefix: string;
  color: string | null;
  assetCount: number;
  boundary: unknown | null;
}
